import type { HiddenCoinRequest, HiddenCoinView } from "@screener/protocol";
import { isRecord } from "@/lib/guards";
import { DAEMON_REQUEST_TIMEOUT_MS } from "@/lib/request-timeout";

/**
 * HTTP-клиент скрытых монет §6.10 запросного канала §7.6. Маршрут один —
 * `/api/hidden-coins` для списка, скрытия и возврата, как у избранного: запись
 * не несёт идентификатора, `DELETE` адресует её той же монетой, какой `POST`
 * создаёт. Тот же стиль различения исходов статусом, что у `favorites-api.ts`.
 */

const HIDDEN_COINS_BASE = "/api/hidden-coins";

function isHiddenCoinView(value: unknown): value is HiddenCoinView {
  return (
    isRecord(value) && typeof value.base === "string" && typeof value.created_at_ms === "number"
  );
}

/** Список скрытых монет предъявившего сессию пользователя, целиком. */
export type HiddenCoinsListResult =
  | { status: "ok"; coins: HiddenCoinView[] }
  | { status: "unauthorized" }
  | { status: "unavailable" };

export async function fetchHiddenCoins(): Promise<HiddenCoinsListResult> {
  let response: Response;
  try {
    response = await fetch(HIDDEN_COINS_BASE, {
      signal: AbortSignal.timeout(DAEMON_REQUEST_TIMEOUT_MS),
    });
  } catch {
    return { status: "unavailable" };
  }
  if (response.status === 401) {
    return { status: "unauthorized" };
  }
  if (!response.ok) {
    console.error("список скрытых монет отклонён демоном", response.status);
    return { status: "unavailable" };
  }
  try {
    const body: unknown = await response.json();
    if (!Array.isArray(body) || !body.every(isHiddenCoinView)) {
      return { status: "unavailable" };
    }
    return { status: "ok", coins: body };
  } catch {
    return { status: "unavailable" };
  }
}

/** Исход скрытия. Демон отвечает `200` и на повтор: скрытие идемпотентно. */
export type HideCoinResult =
  | { status: "ok"; coin: HiddenCoinView }
  | { status: "unauthorized" }
  | { status: "invalid" }
  | { status: "unavailable" };

export async function hideCoin(base: string): Promise<HideCoinResult> {
  const request: HiddenCoinRequest = { base };
  let response: Response;
  try {
    response = await fetch(HIDDEN_COINS_BASE, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(request),
      signal: AbortSignal.timeout(DAEMON_REQUEST_TIMEOUT_MS),
    });
  } catch {
    return { status: "unavailable" };
  }
  if (response.status === 401) {
    return { status: "unauthorized" };
  }
  if (response.status === 400) {
    return { status: "invalid" };
  }
  if (!response.ok) {
    console.error("монета не скрыта демоном", response.status);
    return { status: "unavailable" };
  }
  try {
    const body: unknown = await response.json();
    if (!isHiddenCoinView(body)) {
      return { status: "unavailable" };
    }
    return { status: "ok", coin: body };
  } catch {
    return { status: "unavailable" };
  }
}

/**
 * Исход возврата. Демон отвечает `204` и на отсутствующую монету: желаемое
 * состояние «монета не скрыта» уже достигнуто.
 */
export type UnhideCoinResult =
  | { status: "ok" }
  | { status: "unauthorized" }
  | { status: "invalid" }
  | { status: "unavailable" };

export async function unhideCoin(base: string): Promise<UnhideCoinResult> {
  const request: HiddenCoinRequest = { base };
  let response: Response;
  try {
    response = await fetch(HIDDEN_COINS_BASE, {
      method: "DELETE",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(request),
      signal: AbortSignal.timeout(DAEMON_REQUEST_TIMEOUT_MS),
    });
  } catch {
    return { status: "unavailable" };
  }
  if (response.status === 401) {
    return { status: "unauthorized" };
  }
  if (response.status === 400) {
    return { status: "invalid" };
  }
  if (response.status === 204) {
    return { status: "ok" };
  }
  console.error("монета не возвращена демоном", response.status);
  return { status: "unavailable" };
}
